import { useRouteState } from '../routeState'
import { useEffect, useRef, useState } from 'react'
import { useRefreshGuard } from '../refreshGuards'
import {
  BookOpen,
  MapPin,
  Plus,
  Search,
  Sparkles,
  Trash2,
  Users,
  X,
} from 'lucide-react'
import { searchBible } from '../bibleDiscovery'
import {
  bibleSections,
  blankBible,
  createBibleEntry,
  newId,
  referenceTargets,
} from '../connectedBible'
import type { StoryCharacter, StoryProject } from '../types'
import { StoryVariables } from './StoryLogicEditor'
import { BibleEntryEditor } from './BibleEntryEditor'
import { BibleOverview } from './BibleOverview'

type Props = {
  project: StoryProject
  onChange: (p: StoryProject, key?: string) => void
  focusVariables: boolean
  onSelectNode: (id: string) => void
  onOpenReference: (id: string) => void
  onSaveStory: () => Promise<boolean>
  focusTarget?: string | null
  tourStep?: number | null
}
export function StoryBible({
  project,
  onChange,
  focusVariables,
  onSelectNode,
  onOpenReference,
  onSaveStory,
  focusTarget,
  tourStep,
}: Props) {
  const [section, setSection] = useRouteState<string>('bible-section', 'overview')
  const [selected, setSelected] = useRouteState<string | null>('entry', null)
  const [search, setSearch] = useState('')
  const [newSection, setNewSection] = useState<string | null>(null)
  useRefreshGuard(newSection !== null, 'Finish or cancel your new section before refreshing.')
  const beforeTour = useRef<{ section: string; selected: string | null; search: string } | null>(null)
  const bible = project.bible || blankBible()
  const sections = bibleSections(project)
  const edit = (changes: Partial<typeof bible>, key: string) =>
    onChange({ ...project, bible: { ...bible, ...changes } }, key)
  const chooseSection = (id: string) => {
    setSection(id)
    setSelected(null)
    setSearch('')
  }
  useEffect(() => {
    if (focusVariables) chooseSection('overview')
  }, [focusVariables])
  useEffect(() => {
    if (!focusTarget) return
    const target = referenceTargets(project).find(t => t.id === focusTarget)
    if (!target || target.kind === 'scene') return
    chooseSection(target.kind === 'character' ? 'characters' : bible.entries?.find(e => 'entry:' + e.id === target.id)?.sectionId || 'locations')
    setSelected(target.id)
  }, [focusTarget, project.id])
  useEffect(() => {
    if (tourStep != null) {
      beforeTour.current ||= { section, selected, search }
      if (tourStep > 0) chooseSection('locations')
    } else if (beforeTour.current) {
      setSection(beforeTour.current.section)
      setSelected(beforeTour.current.selected)
      setSearch(beforeTour.current.search)
      beforeTour.current = null
    }
  }, [tourStep])
  const allEntries = referenceTargets(project).filter(
    (t) => t.kind !== 'scene',
  )
  const searching = !!search.trim()
  const visible = searchBible(project, search).filter((t) =>
    searching
      ? true
      : section === 'characters'
        ? t.kind === 'character'
        : bible.entries?.some(
            (e) => 'entry:' + e.id === t.id && e.sectionId === section,
          ),
  )
  const selectedId =
    selected && allEntries.some((t) => t.id === selected)
      ? selected
      : visible[0]?.id
  const sectionTitle =
    section === 'characters'
      ? 'Characters'
      : sections.find((s) => s.id === section)?.title || 'Story foundations'
  const create = () => {
    if (section === 'characters') {
      const character: StoryCharacter = {
        id: newId(),
        name: 'New character',
        role: '',
        description: '',
        voice: '',
      }
      edit({ characters: [...bible.characters, character] }, 'add-character')
      setSelected('character:' + character.id)
    } else {
      const entry = createBibleEntry(
        section === 'locations' ? 'New location' : 'New entry',
        section,
      )
      edit({ entries: [...(bible.entries || []), entry] }, 'add-entry')
      setSelected('entry:' + entry.id)
    }
    setSearch('')
  }
  const scriptNames = [
    ...new Set(
      project.nodes
        .flatMap((n) =>
          n.blocks
            .filter((b) => b.kind === 'dialogue')
            .map((b) => b.speaker?.trim() || ''),
        )
        .filter((n) => n && n !== 'CHARACTER')
        .map((n) => n.toUpperCase()),
    ),
  ]
  const missing = scriptNames.filter(
    (name) =>
      !bible.characters.some(
        (c) => c.name.trim().toLowerCase() === name.toLowerCase(),
      ),
  )
  return (
    <section
      className="bible-studio"
      aria-label="Story bible"
      data-tour="bible"
    >
      <header className="bible-heading">
        <span className="eyebrow">
          <BookOpen size={14} /> THE WORLD BEHIND THE WORDS
        </span>
        <h1>Story bible</h1>
        <p>
          People, places, and the connections that make your world feel real.
        </p>
      </header>
      <nav className="bible-section-tabs" aria-label="Bible sections">
        <button
          type="button"
          aria-pressed={section === 'overview'}
          onClick={() => chooseSection('overview')}
        >
          <BookOpen size={15} /> Overview
        </button>
        <button
          type="button"
          aria-pressed={section === 'characters'}
          onClick={() => chooseSection('characters')}
        >
          <Users size={15} /> Characters{' '}
          <small>{bible.characters.length}</small>
        </button>
        {sections.map((s) => (
          <button
            type="button"
            key={s.id}
            aria-pressed={section === s.id}
            onClick={() => chooseSection(s.id)}
          >
            {s.id === 'locations' && <MapPin size={15} />}
            {s.title}
            <small>
              {bible.entries?.filter((e) => e.sectionId === s.id).length || 0}
            </small>
          </button>
        ))}
        <button
          type="button"
          className="add-section"
          onClick={() => setNewSection('')}
        >
          <Plus size={14} /> New section
        </button>
      </nav>
      <div className="bible-search-bar">
        <label className="bible-search">
          <Search size={16} aria-hidden="true" />
          <input
            aria-label="Search the bible"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value)
              setSelected(null)
            }}
            placeholder="Find a name or a detail you remember…"
          />
        </label>
        {search && (
          <button
            type="button"
            className="button"
            aria-label="Clear bible search"
            onClick={() => {
              setSearch('')
              setSelected(null)
            }}
          >
            <X size={14} /> Clear
          </button>
        )}
        <p role="status">
          {searching
            ? visible.length +
              (visible.length === 1 ? ' match' : ' matches') +
              ' across all bible sections'
            : 'Search names, descriptions, voices, picture captions, and reference links.'}
        </p>
      </div>
      {newSection !== null && (
        <form
          className="new-bible-section"
          onSubmit={(e) => {
            e.preventDefault()
            if (!newSection.trim()) return
            const id = newId()
            edit(
              {
                sections: [
                  ...(bible.sections || []),
                  { id, title: newSection.trim() },
                ],
              },
              'add-section',
            )
            chooseSection(id)
            setNewSection(null)
          }}
        >
          <label>
            Section name
            <input
              autoFocus
              value={newSection}
              maxLength={100}
              onChange={(e) => setNewSection(e.target.value)}
              placeholder="Factions, quests, items, research…"
              required
            />
          </label>
          <button type="submit" className="button button--primary">
            Create section
          </button>
          <button
            type="button"
            className="button"
            onClick={() => setNewSection(null)}
          >
            Cancel
          </button>
        </form>
      )}
      {section === 'overview' && !searching ? (
        <div className="bible-overview">
          <label className="bible-project-title">
            Story title
            <input
              data-live-path={JSON.stringify(["title"])} aria-label="Project title"
              value={project.title}
              onChange={(e) =>
                onChange({ ...project, title: e.target.value }, 'story-title')
              }
            />
          </label>
          <div className="bible-foundations">
            <section className="bible-section">
              <h2>The premise</h2>
              <p>
                What does your character want, and what stands in their way?
              </p>
              <textarea
                data-live-path={JSON.stringify(["bible", "premise"])} aria-label="Story premise"
                rows={6}
                value={bible.premise}
                onChange={(e) =>
                  edit({ premise: e.target.value }, 'bible-premise')
                }
                placeholder="A character wants something. A choice changes everything…"
              />
            </section>
            <section className="bible-section">
              <h2>World & rules</h2>
              <p>
                The history, truths, and rules you want to keep consistent.
              </p>
              <textarea
                data-live-path={JSON.stringify(["bible", "world"])} aria-label="World and rules"
                rows={6}
                value={bible.world}
                onChange={(e) =>
                  edit({ world: e.target.value }, 'bible-world')
                }
                placeholder="What is possible here? What is forbidden?"
              />
            </section>
          </div>
          <section className="bible-section">
            <h2>The notebook</h2>
            <p>Loose ideas, questions, and threads to follow later.</p>
            <textarea
              data-live-path={JSON.stringify(["bible", "notes"])} aria-label="Story notebook"
              rows={8}
              value={bible.notes}
              onChange={(e) => edit({ notes: e.target.value }, 'bible-notes')}
              placeholder="Start anywhere…"
            />
          </section>
          <StoryVariables
            project={project}
            onChange={onChange}
            focusOnOpen={focusVariables}
            onSelectNode={onSelectNode}
          />
          <BibleOverview project={project} />
        </div>
      ) : (
        <>
          <header className="bible-catalog-heading">
            <div>
              <h2>{searching ? 'Search results' : sectionTitle}</h2>
              <p>
                {searching
                  ? 'Open a result to read or edit the detail you found.'
                  : section === 'locations'
                    ? 'Houses, rooms, villages, and everywhere your story goes.'
                    : section === 'characters'
                      ? 'Keep each voice, motivation, and relationship close.'
                      : 'Give these ideas a home in your story.'}
              </p>
            </div>
            {section !== 'overview' && (
              <button
                type="button"
                className="button button--primary"
                onClick={create}
              >
                <Plus size={14} />
                {section === 'characters'
                  ? 'Add character'
                  : section === 'locations'
                    ? 'Add location'
                    : 'Add entry'}
              </button>
            )}
          </header>
          {!searching &&
            section !== 'characters' &&
            section !== 'locations' && (
              <details className="section-settings">
                <summary>Section settings</summary>
                <label>
                  Rename section
                  <input
                    aria-label="Section name"
                    value={sectionTitle}
                    maxLength={100}
                    onChange={(e) =>
                      edit(
                        {
                          sections: bible.sections!.map((s) =>
                            s.id === section
                              ? { ...s, title: e.target.value }
                              : s,
                          ),
                        },
                        'section-name',
                      )
                    }
                  />
                </label>
                <button
                  type="button"
                  className="button danger-action"
                  disabled={
                    !!bible.entries?.some((e) => e.sectionId === section)
                  }
                  onClick={() => {
                    edit(
                      {
                        sections: bible.sections!.filter(
                          (s) => s.id !== section,
                        ),
                      },
                      'remove-section',
                    )
                    chooseSection('overview')
                  }}
                >
                  <Trash2 size={13} /> Remove empty section
                </button>
                <small>
                  Move entries to another section before removing this one.
                </small>
              </details>
            )}
          {!searching && section === 'characters' && !!missing.length && (
            <div className="bible-import">
              <Sparkles size={15} />
              <span>
                {missing.length} script{' '}
                {missing.length === 1
                  ? 'voice needs a profile'
                  : 'voices need profiles'}
                .
              </span>
              <button
                type="button"
                onClick={() =>
                  edit(
                    {
                      characters: [
                        ...bible.characters,
                        ...missing.map((name) => ({
                          id: newId(),
                          name,
                          role: '',
                          description: '',
                          voice: '',
                        })),
                      ],
                    },
                    'import-characters',
                  )
                }
              >
                Add from script
              </button>
            </div>
          )}
          <div className="bible-catalog">
            <aside className="bible-entry-list" aria-label="Bible entries">
              {visible.map((t) => (
                <button
                  type="button"
                  key={t.id}
                  aria-pressed={selectedId === t.id}
                  onClick={() => setSelected(t.id)}
                >
                  <span className="entry-initial">
                    {t.title.slice(0, 1).toUpperCase()}
                  </span>
                  <span>
                    {t.title}
                    <small>{t.section}</small>
                    {t.excerpt &&
                      t.matchLabel !== 'Name' &&
                      t.matchLabel !== 'Section' && (
                        <span className="bible-search-excerpt">
                          <small>{t.matchLabel}</small>
                          {t.excerpt}
                        </span>
                      )}
                  </span>
                </button>
              ))}
              {!visible.length && (
                <p className="empty-message">
                  {searching
                    ? 'No matching entries yet.'
                    : 'Your world starts here. Add the first entry.'}
                </p>
              )}
            </aside>
            <div className="bible-entry-pane">
              {selectedId ? (
                <BibleEntryEditor
                  key={selectedId}
                  project={project}
                  targetId={selectedId}
                  onChange={onChange}
                  onOpen={onOpenReference}
                  onRemoved={() => setSelected(null)}
                  onSaveStory={onSaveStory}
                />
              ) : searching ? (
                <div className="bible-empty">
                  <Search size={32} />
                  <h3>No matching details yet.</h3>
                  <p>
                    Try a shorter phrase, a name, or another word from your
                    notes.
                  </p>
                </div>
              ) : (
                <div className="bible-empty">
                  <BookOpen size={32} />
                  <h3>A little room for a whole world.</h3>
                  <p>
                    Add an entry, then connect it to the people and scenes
                    that belong here.
                  </p>
                  <button type="button" className="button" onClick={create}>
                    <Plus size={14} /> Add your first entry
                  </button>
                </div>
              )}
            </div>
          </div>
        </>
      )}
      <p className="bible-save-note">
        Your bible saves with this story. Use Export → Project backup to take
        entries, images, and connections with you.
      </p>
    </section>
  )
}
